import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getVisitHistory } from '../../../actions/service_request';
import moment from 'moment';
import RenderMilestones from '../components/RenderMileStones';
import IconToolTip from '../../../common/IconToolTip';
import UpdateAvailabilityStatus from '../../team/Modals/UpdateAvailabilityStatus';
import { canUpdateUserCalender, canViewUserCalender } from '../../../utils/access';

const getAttendanceUser = (log, serviceRequest) => {
    const visit = log?.visit || {};
    const agentName = log?.agent_name || visit?.agent_name || log?.agent?.full_name || visit?.agent?.full_name || '';
    const agentId = log?.agent_id
        || log?.user_id
        || log?.agent?.id
        || visit?.agent_id
        || visit?.user_id
        || visit?.agent?.id;

    const assignedAgent = serviceRequest?.agents?.find(({ agent }) => {
        if (agentId) {
            return `${agent?.id}` === `${agentId}`;
        }
        return agentName && [agent?.full_name, agent?.name].includes(agentName);
    })?.agent;

    return {
        id: agentId || assignedAgent?.id,
        fullName: agentName || assignedAgent?.full_name || assignedAgent?.name || 'User',
    };
};

const TravelLogModal = ({ ticketId }) => {
    const dispatch = useDispatch();
    const visitHistory = useSelector((state) => state.serviceRequest.visitHistory);
    const isVisitHistoryLoading = useSelector((state) => state.loading.isVisitHistoryLoading);
    const serviceRequestList = useSelector((state) => state.serviceRequest.serviceRequestList);
    const loggedInUserActions = useSelector((state) => state.auth.newLoggedUserData?.actions);
    const [selectedMilestone, setSelectedMilestone] = useState(null);
    const [selectedAttendanceUser, setSelectedAttendanceUser] = useState(null);

    const serviceRequest = useMemo(() => {
        if (!serviceRequestList) return null;
        return serviceRequestList.data.find(ticket => ticket.id === ticketId);
    }, [ticketId, serviceRequestList]);

    const history = useMemo(() => {
        let h = [];
        if (visitHistory?.[ticketId]) {
            h = visitHistory[ticketId].map(item => {
                let visit = item.visits.find((elem) => elem.check_out_time && elem.check_in_time)
                if (!visit) {
                    visit = item.visits[0];
                }

                // Convert check_in and check_out times to Indian time (+5:30)
                if (visit) {
                    const convertedVisit = { ...visit };

                    // If check_in_time and check_out_time are in UTC, convert them to IST (India time)
                    // Works for ISO strings like: 2025-12-29T11:24:17.227Z
                    // IST = UTC + 05:30

                    if (visit.check_in_time) {
                        const checkInMoment = moment.utc(visit.check_in_time).add(5, 'hours').add(30, 'minutes');
                        // Keep original format with milliseconds if present, else just show HH:mm:ss
                        if (visit.check_in_time.includes('.')) {
                            convertedVisit.check_in_time = checkInMoment.format('HH:mm:ss.SSS');
                        } else {
                            convertedVisit.check_in_time = checkInMoment.format('HH:mm:ss');
                        }
                    }

                    if (visit.check_out_time) {
                        const checkOutMoment = moment.utc(visit.check_out_time).add(5, 'hours').add(30, 'minutes');
                        if (visit.check_out_time.includes('.')) {
                            convertedVisit.check_out_time = checkOutMoment.format('HH:mm:ss.SSS');
                        } else {
                            convertedVisit.check_out_time = checkOutMoment.format('HH:mm:ss');
                        }
                    }

                    return { ...item, visit: convertedVisit };
                }

                return { ...item, visit };
            });
        }
        return h;
    }, [visitHistory, ticketId]);


    const milestoneDetails = useMemo(() => {
        if (!serviceRequest?.milestones || !selectedMilestone) return null;
        return serviceRequest.milestones.find(milestone => milestone.id === selectedMilestone)?.metadata;
    }, [serviceRequest?.milestones, selectedMilestone]);

    useEffect(() => {
        if (ticketId) {
            dispatch(getVisitHistory({ ticketId }));
        }
    }, [ticketId, dispatch]);


    const eligibleMilestones = useMemo(() => {
        return serviceRequest?.milestones?.filter(milestone => milestone?.metadata?.raise_sign_off_from || milestone?.metadata?.time_spent_at_site);
    }, [serviceRequest?.milestones]);

    useEffect(() => {
        if (eligibleMilestones?.length > 0) {
            setSelectedMilestone(eligibleMilestones[0].id);
        }
    }, [eligibleMilestones]);

    const canViewAttendance = canViewUserCalender(loggedInUserActions) || canUpdateUserCalender(loggedInUserActions);

    const handleViewAttendance = (attendanceUser) => {
        setSelectedAttendanceUser(attendanceUser);
    };

    if (isVisitHistoryLoading) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="flex flex-col items-center gap-2">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-scogoprimary"></div>
                    <p className="text-font10 text-gray-500">Loading travel log...</p>
                </div>
            </div>
        );
    }

    if (selectedAttendanceUser) {
        return (
            <div className='flex h-full min-h-0 flex-col overflow-hidden rounded-lg bg-slate-50'>
                <div className='flex flex-shrink-0 items-center gap-3 border-b border-blue-100 bg-white px-4 py-3 shadow-sm'>
                    <button
                        type='button'
                        onClick={() => setSelectedAttendanceUser(null)}
                        className='group inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-scogoprimary transition-colors hover:bg-blue-50 hover:text-scogoorange'
                        aria-label='Back to Travel Log'
                    >
                        <span className='material-icons text-font18 transition-transform group-hover:-translate-x-0.5'>arrow_back</span>
                        <span className='text-font11 font-semibold'>Back to Travel Log</span>
                    </button>
                    <div className='h-6 w-px bg-gray-200' />
                    <div className='min-w-0'>
                        <p className='text-font09 font-medium uppercase tracking-wide text-gray-400'>View Attendance</p>
                        <p className='truncate text-font12 font-semibold capitalize text-gray-800'>{selectedAttendanceUser.fullName}</p>
                    </div>
                </div>
                <div className='min-h-0 flex-1 overflow-y-auto'>
                    <UpdateAvailabilityStatus
                        user_id={selectedAttendanceUser.id}
                        full_name={selectedAttendanceUser.fullName}
                        actions={[]}
                    />
                </div>
            </div>
        );
    }


    return (
        <div className="flex flex-col h-full px-4 py-3">
            {eligibleMilestones?.length > 1 ? <RenderMilestones milestones={eligibleMilestones} selectedMilestones={selectedMilestone} setSelectedMilestones={setSelectedMilestone} agents={serviceRequest?.agents} /> : null}
            {/* Stats Cards */}
            <div className={`grid grid-cols-1 md:grid-cols-3 gap-3 mb-4 ${serviceRequest?.milestones?.length > 1 ? 'mt-2' : ''}`}>
                {milestoneDetails?.raise_sign_off_from ? <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-2 border border-blue-100 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-2">
                        <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center shadow-md">
                            <span className="material-icons text-white text-xl">phone_android</span>
                        </div>
                        <div className="flex-1">
                            <p className="text-font10 text-gray-600 font-medium mb-0.5">Update Sign Off From</p>
                            <p className="text-font12 text-gray-900 font-bold">
                                {milestoneDetails?.raise_sign_off_from?.replace(/_/g, ' ').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')}
                            </p>
                        </div>
                    </div>
                </div> : null}

                {milestoneDetails?.time_spent_at_site ? <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-2 border border-green-100 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-2">
                        <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center shadow-md">
                            <span className="material-icons text-white text-xl">access_time</span>
                        </div>
                        <div className="flex-1">
                            <p className="text-font10 text-gray-600 font-medium mb-0.5">Time Spent At Site</p>
                            <p className="text-font12 text-gray-900 font-bold">{milestoneDetails?.time_spent_at_site ? Math.round(milestoneDetails.time_spent_at_site / 60000) : 0} min</p>
                        </div>
                    </div>
                </div> : null}

                <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-2 border border-purple-100 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-2">
                        <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center shadow-md">
                            <span className="material-icons text-white text-xl">location_on</span>
                        </div>
                        <div className="flex-1">
                            <p className="text-font10 text-gray-600 font-medium mb-0.5">Total Visits</p>
                            <p className="text-font12 text-gray-900 font-bold">{history.length}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Travel Log Table */}
            <div className="flex-1 overflow-hidden bg-white rounded-lg border border-gray-200 shadow-sm">
                {history.length > 0 ? (
                    <div className="overflow-y-auto h-full">
                        <table className="w-full">
                            <thead className="sticky top-0 bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-200 z-10">
                                <tr>
                                    <th className="px-3 py-2 text-left text-font10 font-bold text-gray-700 uppercase tracking-wider">
                                        <div className="flex items-center gap-1.5">
                                            <span className="material-icons text-font12">calendar_today</span>
                                            Date
                                        </div>
                                    </th>
                                    <th className="px-3 py-2 text-left text-font10 font-bold text-gray-700 uppercase tracking-wider">
                                        <div className="flex items-center gap-1.5">
                                            <span className="material-icons text-font12">person</span>
                                            Agent Name
                                        </div>
                                    </th>
                                    <th className="px-3 py-2 text-left text-font10 font-bold text-gray-700 uppercase tracking-wider">
                                        <div className="flex items-center gap-1.5">
                                            <span className="material-icons text-font12">login</span>
                                            Check In
                                        </div>
                                    </th>
                                    <th className="px-3 py-2 text-left text-font10 font-bold text-gray-700 uppercase tracking-wider">
                                        <div className="flex items-center gap-1.5">
                                            <span className="material-icons text-font12">logout</span>
                                            Check Out
                                        </div>
                                    </th>
                                    <th className="px-3 py-2 text-left text-font10 font-bold text-gray-700 uppercase tracking-wider">
                                        <div className="flex items-center gap-1.5">
                                            Updated Check In From
                                        </div>
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {history.map((log, idx) => {
                                    const attendanceUser = getAttendanceUser(log, serviceRequest);
                                    return (
                                        <tr
                                            key={`${log.date}-${idx}`}
                                            className={`hover:bg-blue-50 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}
                                        >
                                            <td className="px-3 py-2.5 text-font10 text-gray-700 font-medium">
                                                <div className="flex items-center gap-1.5">
                                                    <span className="w-1.5 h-1.5 bg-scogoprimary rounded-full"></span>
                                                    {log.date || ''}
                                                </div>
                                            </td>
                                            <td className="px-3 py-2.5 text-font10 text-gray-700">
                                                <div className="flex items-center justify-between gap-2">
                                                    <span className="font-medium capitalize">{log.agent_name || log.visit?.agent_name || ''}</span>
                                                    {canViewAttendance && attendanceUser.id ? (
                                                        <IconToolTip title='View Attendance'>
                                                            <button
                                                                type='button'
                                                                aria-label={`View Attendance for ${attendanceUser.fullName}`}
                                                                className='w-7 h-7 flex-shrink-0 inline-flex items-center justify-center rounded-full bg-blue-50 text-scogoprimary hover:bg-blue-100 hover:text-scogoorange transition-colors'
                                                                onClick={() => handleViewAttendance(attendanceUser)}
                                                            >
                                                                <span className='material-icons text-font15'>event_available</span>
                                                            </button>
                                                        </IconToolTip>
                                                    ) : null}
                                                </div>
                                            </td>
                                            <td className="px-3 py-2.5 text-font10 text-gray-700">
                                                <div className="flex items-center gap-1.5">
                                                    <span className="w-7 h-7 bg-green-100 rounded-full flex items-center justify-center">
                                                        <span className="material-icons text-green-600 text-font11">arrow_downward</span>
                                                    </span>
                                                    <span className="font-medium">{log.visit?.check_in_time || ''}</span>
                                                </div>
                                            </td>
                                            <td className="px-3 py-2.5 text-font10 text-gray-700">
                                                <div className="flex items-center gap-1.5">
                                                    <span className="w-7 h-7 bg-red-100 rounded-full flex items-center justify-center">
                                                        <span className="material-icons text-red-600 text-font11">arrow_upward</span>
                                                    </span>
                                                    <span className="font-medium">{log.visit?.check_out_time || ''}</span>
                                                </div>
                                            </td>
                                            <td className="px-3 py-2.5 text-font10 text-gray-700">
                                                <div className="flex items-center gap-1.5 text-gray-700 font-medium">
                                                    {log?.visit?.reached_site_from ? log?.visit?.reached_site_from?.replace(/_/g, ' ').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ') : ''}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center h-full py-10">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                            <span className="material-icons text-3xl text-gray-400">location_off</span>
                        </div>
                        <p className="text-font12 text-gray-500 font-medium mb-1">No travel log available</p>
                        <p className="text-font10 text-gray-400">Visit history will appear here once visits are recorded</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TravelLogModal;
